/**
 * Primer uso: llevar a alguien de una cuenta vacía a poder registrar su primera serie.
 *
 * Existe porque sin esto la app tenía un callejón sin salida: una cuenta nueva caía en un Inicio
 * vacío, el registro decía "cargá una rutina en Ajustes", y en Ajustes no había editor de rutinas.
 * Nadie podía usar la app sin que se le explicara, que es exactamente el problema a resolver.
 *
 * Cuatro pasos, y todos salteables salvo el gimnasio y la rutina, que son lo mínimo para registrar:
 * perfil → gimnasio → rutina → plan → listo.
 *
 * Dos cosas que no son obvias y que este archivo sostiene:
 *
 * - **El borrador sobrevive a cerrar la app.** Es el tramo más largo del producto y lo único que no
 *   se puede guardar a medias, así que va a `localStorage` en cada cambio.
 * - **Nada se guarda si el gimnasio no pudo subir.** El gimnasio es compartido y el primer pull
 *   reemplaza esa tabla entera: guardarlo sólo en local no lo salva, lo condena.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CATALOGO_BASE,
  PLANTILLAS,
  addDias,
  alturaDesdeIMC,
  plantillaPorId,
  type Ejercicio,
  type Fecha,
  type Gimnasio,
  type PlantillaRutina,
  type Sexo,
} from '@mg/core';

import { ErrorAPI, pedir } from '../datos/api.ts';
import { db, guardarLocal } from '../datos/db.ts';
import { sincronizar } from '../datos/sync.ts';
import { useDatos } from './useDatos.ts';

export type PasoOnboarding = 'perfil' | 'gimnasio' | 'rutina' | 'plan' | 'listo';

export const PASOS: { id: PasoOnboarding; titulo: string; para: string }[] = [
  { id: 'perfil', titulo: 'Quién sos', para: 'Para calcular el FFMI hace falta tu altura.' },
  { id: 'gimnasio', titulo: 'Dónde entrenás', para: 'Los días que cierra salen del cálculo de tu plan.' },
  { id: 'rutina', titulo: 'Qué entrenás', para: 'Elegí un punto de partida y sacale o agregale lo que quieras.' },
  { id: 'plan', titulo: 'Tu plan', para: 'Si compraste un paquete de sesiones, la app te avisa si te alcanza el tiempo.' },
];

const DIAS_SEMANA = [
  { n: 1, etiqueta: 'Lun' },
  { n: 2, etiqueta: 'Mar' },
  { n: 3, etiqueta: 'Mié' },
  { n: 4, etiqueta: 'Jue' },
  { n: 5, etiqueta: 'Vie' },
  { n: 6, etiqueta: 'Sáb' },
  { n: 0, etiqueta: 'Dom' },
];

const nuevoId = (p: string): string => `${p}-${crypto.randomUUID()}`;

/** Un día de rutina que se está armando. */
export interface DiaEnArmado {
  nombre: string;
  ejercicios: string[];
}

/**
 * El borrador del onboarding, en localStorage.
 *
 * Sin esto, cerrar la app en el paso 3 —o que el sistema mate la pestaña, que en un teléfono pasa
 * solo— tira la plantilla elegida, los días armados y el gimnasio, y hay que empezar de cero. Es el
 * tramo más largo de la app y el único que no se puede guardar a medias: la rutina recién existe
 * cuando está entera, así que hasta entonces no hay nada en la planilla que la sostenga.
 */
const CLAVE_BORRADOR = 'mg.onboarding.borrador';

interface Borrador {
  paso: PasoOnboarding;
  nombre: string;
  altura: string;
  sexo: Sexo;
  unidad: 'kg' | 'lb';
  nombreGimnasio: string;
  diasCierre: number[];
  gimnasioExistente: string | null;
  plantillaId: string | null;
  dias: DiaEnArmado[];
  seriesObjetivo: number;
  repsMin: number;
  repsMax: number;
  conPlan: boolean;
  sesionesTotales: string;
  vencimiento: Fecha;
}

function leerBorrador(): Borrador | null {
  try {
    const crudo = localStorage.getItem(CLAVE_BORRADOR);
    if (!crudo) return null;
    const b = JSON.parse(crudo) as Partial<Borrador> | null;
    // Un borrador incompleto —de una versión anterior del wizard, o escrito a mano— no puede
    // tumbar el primer uso: se descarta entero y se arranca limpio.
    if (!b || typeof b !== 'object' || !Array.isArray(b.dias)) return null;
    return b as Borrador;
  } catch {
    return null;
  }
}

/**
 * Se llama al terminar el onboarding y **al salir de la cuenta**: el borrador lleva altura, sexo y
 * gimnasio, y este dispositivo puede ser compartido. Lo mismo que hace `limpiarTodo()` con la base
 * local, por la misma razón.
 */
export function borrarBorradorOnboarding(): void {
  try {
    localStorage.removeItem(CLAVE_BORRADOR);
  } catch {
    // Sin localStorage nunca hubo borrador que borrar.
  }
}

export function useOnboarding(alTerminar: () => void) {
  const datos = useDatos();

  const [borrador] = useState(leerBorrador);
  const [paso, setPaso] = useState<PasoOnboarding>(() =>
    borrador && PASOS.some((p) => p.id === borrador.paso) ? borrador.paso : 'perfil',
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Perfil -------------------------------------------------------------
  const [nombre, setNombre] = useState(() => borrador?.nombre ?? datos.perfil?.nombre ?? '');
  const [altura, setAltura] = useState(() => borrador?.altura ?? String(datos.perfil?.altura_cm ?? ''));
  const [sexo, setSexo] = useState<Sexo>(() => borrador?.sexo ?? datos.perfil?.sexo ?? 'otro');
  const [unidad, setUnidad] = useState<'kg' | 'lb'>(
    () => borrador?.unidad ?? datos.perfil?.unidad_preferida ?? 'kg',
  );

  // El perfil puede llegar DESPUÉS del primer render: en una cuenta recién creada, hasta que el
  // primer pull no baja la planilla no hay perfil local. Sin esto, el nombre que la persona acaba
  // de escribir al registrarse aparecería en blanco. Mientras tanto no se muestra el wizard (ver
  // `esperandoPlanilla`), así que sembrar acá no puede pisar nada que el usuario haya tipeado.
  const perfilPorSembrar = useRef(borrador === null && datos.perfil === null);
  useEffect(() => {
    const p = datos.perfil;
    if (!perfilPorSembrar.current || !p) return;
    perfilPorSembrar.current = false;
    setNombre(p.nombre);
    setAltura(String(p.altura_cm ?? ''));
    setSexo(p.sexo);
    setUnidad(p.unidad_preferida);
  }, [datos.perfil]);

  const alturaSugerida = useMemo(() => {
    const conIMC = [...datos.medidas].reverse().find((m) => m.imc !== null && m.peso > 0);
    return conIMC?.imc ? alturaDesdeIMC(conIMC.peso, conIMC.imc) : null;
  }, [datos.medidas]);

  // --- Gimnasio -----------------------------------------------------------
  const [nombreGimnasio, setNombreGimnasio] = useState(() => borrador?.nombreGimnasio ?? '');
  // Domingo preseleccionado: es de lejos el día que más gimnasios cierran.
  const [diasCierre, setDiasCierre] = useState<number[]>(() => borrador?.diasCierre ?? [0]);
  const [gimnasioExistente, setGimnasioExistente] = useState<string | null>(
    () => borrador?.gimnasioExistente ?? null,
  );
  /** El gimnasio que se armó y no se pudo subir. Se reintenta con el mismo id, no con uno nuevo. */
  const [gimnasioPendiente, setGimnasioPendiente] = useState<Gimnasio | null>(null);

  const alternarDia = useCallback((n: number) => {
    setDiasCierre((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
  }, []);

  // --- Rutina -------------------------------------------------------------
  const [plantillaId, setPlantillaId] = useState<string | null>(() => borrador?.plantillaId ?? null);
  const [dias, setDias] = useState<DiaEnArmado[]>(() => borrador?.dias ?? []);
  const [seriesObjetivo, setSeriesObjetivo] = useState(() => borrador?.seriesObjetivo ?? 3);
  const [repsMin, setRepsMin] = useState(() => borrador?.repsMin ?? 10);
  const [repsMax, setRepsMax] = useState(() => borrador?.repsMax ?? 12);

  const elegirPlantilla = useCallback((p: PlantillaRutina | null) => {
    setPlantillaId(p?.id ?? null);
    setDias(
      p
        ? p.dias.map((d) => ({ nombre: d.nombre, ejercicios: [...d.ejercicios] }))
        : [{ nombre: 'Día 1', ejercicios: [] }],
    );
  }, []);

  const quitarEjercicio = useCallback((iDia: number, ejercicioId: string) => {
    setDias((prev) =>
      prev.map((d, i) => (i === iDia ? { ...d, ejercicios: d.ejercicios.filter((e) => e !== ejercicioId) } : d)),
    );
  }, []);

  const agregarEjercicio = useCallback((iDia: number, ejercicioId: string) => {
    setDias((prev) =>
      prev.map((d, i) =>
        i === iDia && !d.ejercicios.includes(ejercicioId)
          ? { ...d, ejercicios: [...d.ejercicios, ejercicioId] }
          : d,
      ),
    );
  }, []);

  const renombrarDia = useCallback((iDia: number, nombre: string) => {
    setDias((prev) => prev.map((d, i) => (i === iDia ? { ...d, nombre } : d)));
  }, []);

  const agregarDia = useCallback(() => {
    setDias((prev) => [...prev, { nombre: `Día ${prev.length + 1}`, ejercicios: [] }]);
  }, []);

  const quitarDia = useCallback((iDia: number) => {
    setDias((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== iDia)));
  }, []);

  // --- Plan ---------------------------------------------------------------
  const [conPlan, setConPlan] = useState(() => borrador?.conPlan ?? true);
  const [sesionesTotales, setSesionesTotales] = useState(() => borrador?.sesionesTotales ?? '12');
  const [vencimiento, setVencimiento] = useState<Fecha>(
    () => borrador?.vencimiento ?? addDias(datos.hoy, 30),
  );

  // --- El borrador, escrito en un solo lugar -------------------------------
  // Serializar en un efecto y no en cada setter: es una escritura por cambio y ningún setter nuevo
  // puede olvidarse de hacerla.
  useEffect(() => {
    if (paso === 'listo') return;
    try {
      localStorage.setItem(
        CLAVE_BORRADOR,
        JSON.stringify({
          paso,
          nombre,
          altura,
          sexo,
          unidad,
          nombreGimnasio,
          diasCierre,
          gimnasioExistente,
          plantillaId,
          dias,
          seriesObjetivo,
          repsMin,
          repsMax,
          conPlan,
          sesionesTotales,
          vencimiento,
        } satisfies Borrador),
      );
    } catch {
      // Modo privado o cuota llena: el onboarding funciona igual, sólo que no sobrevive a cerrar
      // la app. No es motivo para interrumpir a nadie.
    }
  }, [
    paso,
    nombre,
    altura,
    sexo,
    unidad,
    nombreGimnasio,
    diasCierre,
    gimnasioExistente,
    plantillaId,
    dias,
    seriesObjetivo,
    repsMin,
    repsMax,
    conPlan,
    sesionesTotales,
    vencimiento,
  ]);

  // --- Esperando la planilla ----------------------------------------------
  const [sincronizando, setSincronizando] = useState(false);
  const [errorPlanilla, setErrorPlanilla] = useState<string | null>(null);

  const reintentarPlanilla = useCallback(async (): Promise<void> => {
    setSincronizando(true);
    setErrorPlanilla(null);
    try {
      await sincronizar();
    } catch (e) {
      // El mensaje genérico de sync —"tus datos están guardados en el teléfono"— acá sería mentira:
      // el problema es justamente que todavía no hay nada guardado en el teléfono.
      const sinRed = e instanceof ErrorAPI && e.estado === 0;
      setErrorPlanilla(
        sinRed
          ? 'Sin conexión. Tu planilla está en el servidor, pero todavía no llegó a este dispositivo.'
          : e instanceof Error
            ? e.message
            : 'No se pudo traer tu planilla.',
      );
    } finally {
      setSincronizando(false);
    }
  }, []);

  // --- Catálogo disponible ------------------------------------------------
  // El catálogo local puede estar vacío si nunca sincronizó: se cae al de core, que es el mismo
  // que la API siembra. Así el onboarding funciona incluso sin haber hecho un pull.
  const catalogo: Ejercicio[] = datos.ejercicios.length > 0 ? datos.ejercicios : CATALOGO_BASE;

  const catalogoPorId = useMemo(() => new Map(catalogo.map((e) => [e.id, e])), [catalogo]);

  // --- Validación por paso ------------------------------------------------
  const puedeSeguir = useMemo(() => {
    if (paso === 'perfil') return nombre.trim().length > 0;
    if (paso === 'gimnasio') return gimnasioExistente !== null || nombreGimnasio.trim().length > 0;
    if (paso === 'rutina') return dias.length > 0 && dias.every((d) => d.ejercicios.length > 0);
    if (paso === 'plan') return !conPlan || (Number(sesionesTotales) > 0 && vencimiento > datos.hoy);
    return true;
  }, [paso, nombre, gimnasioExistente, nombreGimnasio, dias, conPlan, sesionesTotales, vencimiento, datos.hoy]);

  /** Qué falta, para no dejar un botón deshabilitado sin explicación. */
  const queFalta = useMemo((): string | null => {
    if (puedeSeguir) return null;
    if (paso === 'perfil') return 'Falta tu nombre.';
    if (paso === 'gimnasio') return 'Ponele un nombre a tu gimnasio, o elegí uno de la lista.';
    if (paso === 'rutina') {
      const vacios = dias.filter((d) => d.ejercicios.length === 0).map((d) => d.nombre);
      return vacios.length > 0 ? `Estos días quedaron sin ejercicios: ${vacios.join(', ')}.` : 'Agregá al menos un día.';
    }
    if (paso === 'plan') return 'Revisá la cantidad de sesiones y que el vencimiento sea futuro.';
    return null;
  }, [puedeSeguir, paso, dias]);

  // --- Guardar todo -------------------------------------------------------

  const terminar = useCallback(async (): Promise<void> => {
    if (!datos.perfil) return;
    setGuardando(true);
    setError(null);

    try {
      // El gimnasio va PRIMERO, y si no sube no se sigue.
      //
      // Es compartido: vive en la planilla común y se crea por el endpoint del catálogo, no por la
      // cola de sync. La versión anterior lo guardaba sólo en local diciendo "se sube cuando vuelva
      // la conexión", y era falso: `pull()` reemplaza la tabla de gimnasios entera, así que el
      // primer sync lo borraba y el onboarding reaparecía pidiendo todo de nuevo. Antes que guardar
      // algo que el próximo sync va a tirar, se dice que hace falta conexión una vez.
      let gimnasioId = gimnasioExistente;
      if (!gimnasioId) {
        const gimnasio: Gimnasio = gimnasioPendiente ?? {
          id: nuevoId('g'),
          nombre: nombreGimnasio.trim(),
          notas: diasCierre.length > 0 ? `Cierra: ${diasCierre.map((d) => DIAS_SEMANA.find((x) => x.n === d)?.etiqueta).join(', ')}` : '',
          dias_cierre_semanal: [...diasCierre].sort((a, b) => a - b),
        };
        try {
          await pedir('/catalogo/gimnasios', { metodo: 'POST', cuerpo: { gimnasios: [gimnasio] } });
        } catch {
          // Se guarda el gimnasio armado, no el error: al reintentar sube el MISMO id y no queda
          // un gimnasio duplicado por cada intento.
          setGimnasioPendiente(gimnasio);
          setError('Crear el gimnasio necesita conexión una sola vez.');
          return;
        }
        await db.gimnasios.put(gimnasio);
        setGimnasioPendiente(null);
        gimnasioId = gimnasio.id;
      }

      const alturaNum = Number(altura);

      await guardarLocal('perfil', [
        {
          ...datos.perfil,
          nombre: nombre.trim(),
          altura_cm: Number.isFinite(alturaNum) && alturaNum > 0 ? alturaNum : null,
          sexo,
          unidad_preferida: unidad,
        },
      ]);

      // Rutina, días y ejercicios.
      const rutinaId = nuevoId('r');
      await guardarLocal('rutinas', [
        {
          id: rutinaId,
          user_id: datos.perfil.id,
          nombre: plantillaId ? (plantillaPorId(plantillaId)?.nombre ?? 'Mi rutina') : 'Mi rutina',
          activa: true,
        },
      ]);

      const filasDias = dias.map((d, i) => ({
        id: nuevoId('rd'),
        rutina_id: rutinaId,
        orden: i + 1,
        nombre: d.nombre.trim() || `Día ${i + 1}`,
      }));
      await guardarLocal('rutina_dias', filasDias);

      const filasEjercicios = dias.flatMap((d, i) =>
        d.ejercicios.map((ejercicioId, j) => ({
          id: nuevoId('re'),
          rutina_dia_id: filasDias[i]!.id,
          ejercicio_id: ejercicioId,
          orden: j + 1,
          series_objetivo: seriesObjetivo,
          reps_objetivo_min: repsMin,
          reps_objetivo_max: repsMax,
        })),
      );
      await guardarLocal('rutina_ejercicios', filasEjercicios);

      if (conPlan) {
        await guardarLocal('planes', [
          {
            id: nuevoId('p'),
            user_id: datos.perfil.id,
            gimnasio_id: gimnasioId,
            sesiones_totales: Number(sesionesTotales),
            fecha_inicio: datos.hoy,
            fecha_vencimiento: vencimiento,
          },
        ]);
      }

      // El borrador ya no tiene para qué existir: lo que había adentro está guardado.
      borrarBorradorOnboarding();
      // `alTerminar()` NO se llama acá: lo llama el usuario desde el paso "listo". Encadenarlo con
      // el guardado hacía que la pantalla final no se viera nunca, porque los datos que la
      // disparan son los mismos que hacen innecesario el onboarding.
      setPaso('listo');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar la configuración.');
    } finally {
      setGuardando(false);
    }
  }, [
    datos.perfil,
    datos.hoy,
    nombre,
    altura,
    sexo,
    unidad,
    gimnasioExistente,
    gimnasioPendiente,
    nombreGimnasio,
    diasCierre,
    plantillaId,
    dias,
    seriesObjetivo,
    repsMin,
    repsMax,
    conPlan,
    sesionesTotales,
    vencimiento,
  ]);

  const indice = PASOS.findIndex((p) => p.id === paso);

  const siguiente = useCallback(() => {
    if (paso === 'plan') {
      void terminar();
      return;
    }
    const next = PASOS[indice + 1];
    if (next) {
      // El error es del paso en el que se produjo: arrastrarlo a otro paso es confundir a alguien
      // que ya se movió del problema.
      setError(null);
      setPaso(next.id);
    }
  }, [paso, indice, terminar]);

  const anterior = useCallback(() => {
    const prev = PASOS[indice - 1];
    if (prev) {
      setError(null);
      setPaso(prev.id);
    }
  }, [indice]);

  /** Reintenta el guardado completo; con un gimnasio pendiente, reintenta su POST y sigue. */
  const reintentar = useCallback(() => {
    void terminar();
  }, [terminar]);

  /** Cierra el onboarding y suelta al usuario en la app. Lo toca él, en el último paso. */
  const cerrar = useCallback(() => {
    alTerminar();
  }, [alTerminar]);

  return {
    datos,
    paso,
    setPaso,
    indice,
    pasos: PASOS,
    puedeSeguir,
    queFalta,
    siguiente,
    anterior,
    cerrar,
    guardando,
    error,
    /** El error tiene reintento propio: falta subir el gimnasio y nada más. */
    puedeReintentar: gimnasioPendiente !== null,
    reintentar,

    /** Hay sesión pero la planilla todavía no llegó: no hay nada que pintar. */
    esperandoPlanilla: esperandoPlanilla(datos),
    sincronizando,
    errorPlanilla,
    reintentarPlanilla,

    nombre,
    setNombre,
    altura,
    setAltura,
    alturaSugerida,
    sexo,
    setSexo,
    unidad,
    setUnidad,

    nombreGimnasio,
    setNombreGimnasio,
    diasCierre,
    alternarDia,
    diasSemana: DIAS_SEMANA,
    gimnasioExistente,
    setGimnasioExistente,
    gimnasiosDisponibles: datos.gimnasios,

    plantillas: PLANTILLAS,
    plantillaId,
    elegirPlantilla,
    dias,
    renombrarDia,
    agregarDia,
    quitarDia,
    agregarEjercicio,
    quitarEjercicio,
    catalogo,
    catalogoPorId,
    seriesObjetivo,
    setSeriesObjetivo,
    repsMin,
    setRepsMin,
    repsMax,
    setRepsMax,

    conPlan,
    setConPlan,
    sesionesTotales,
    setSesionesTotales,
    vencimiento,
    setVencimiento,
  };
}

/**
 * ¿Hace falta el onboarding? Sí cuando falta lo mínimo para poder registrar: un gimnasio o una
 * rutina con al menos un ejercicio. El plan y la altura son opcionales.
 *
 * Sin perfil devuelve false a propósito: ahí todavía no se sabe si falta algo o si sólo falta que
 * llegue la planilla. Ese caso es `esperandoPlanilla`.
 */
export function necesitaOnboarding(datos: ReturnType<typeof useDatos>): boolean {
  if (datos.cargando) return false;
  if (!datos.perfil) return false;
  if (datos.gimnasios.length === 0) return true;
  if (datos.rutinaDias.length === 0) return true;
  return datos.rutinaEjercicios.length === 0;
}

/**
 * ¿Hay sesión abierta y todavía ningún perfil local?
 *
 * Pasa con una cuenta recién creada hasta que el primer pull baja la planilla, y en cada
 * dispositivo nuevo. Mientras dura, TODA la app se pinta vacía: sin perfil no hay unidades, ni
 * zona horaria, ni "hoy" que valga. Y si nunca hay red, se queda así para siempre sin decir por
 * qué. Vale la pena una pantalla propia que lo diga y ofrezca reintentar.
 */
export function esperandoPlanilla(datos: ReturnType<typeof useDatos>): boolean {
  return !datos.cargando && datos.perfil === null;
}
